import { OrthographicCamera, Quaternion, Vector3 } from 'three';

import { SceneObjectBounds } from '@paragon/three-shared';

import { CameraType } from '../store/slices/camera/types';
import { ANTI_ZOOM_FACTOR_PER_VIEWPORT_HEIGHT, updateAntiZoomFactor } from './antiZoom';
import { DesignClientCameraMethods } from './DesignClientCamera';
import { getSize } from './reactThreeFiberBindings';

const FOCUS_ZOOM_PADDING = 0.1;

const HEIGHT = 150;

const NEAR_FRUSTUM = 0.1;
const FAR_FRUSTUM = 1000;

const Z_POSITION = 50;

const ZOOM_MAX_CAP = 60;

/**
 * This value serves two purposes:
 *  - This is the minimum allowed zoom if there is no active component.
 *  - The user should always be able to zoom out this much. The active component should only ever allow the user to zoom
 *    out more than this value, not less.
 */
const DEFAULT_ZOOM_MIN_CAP = 1;

/**
 * The factor to multiply the bounding box size to get the min zoom cap.
 * For example, if the component has a span of 12' feet, the user should be able to zoom out to a width of 36' feet.
 */
const ZOOM_MIN_CAP_FACTOR = 3;

export interface BoundingBoxSize {
  readonly width: number;
  readonly height: number;
}

export class OrthographicDesignCamera extends OrthographicCamera implements DesignClientCameraMethods {
  public type: CameraType = 'orthographic-design';

  public aspect: number;

  private boundingBoxSizeOfActiveComponent: BoundingBoxSize | null;

  constructor(
    aspectRatio: number,
    zPositionShouldBePositive: boolean,
    initialRotation: Quaternion,
    boundingBoxSizeOfActiveComponent: BoundingBoxSize | null,
  ) {
    super((HEIGHT * aspectRatio) / -2, (HEIGHT * aspectRatio) / 2, HEIGHT / 2, HEIGHT / -2, NEAR_FRUSTUM, FAR_FRUSTUM);

    this.aspect = aspectRatio;
    this.boundingBoxSizeOfActiveComponent = boundingBoxSizeOfActiveComponent;

    this.position.setZ(zPositionShouldBePositive ? Z_POSITION : -Z_POSITION);
    this.rotation.setFromQuaternion(initialRotation);

    this.updateProjectionMatrix();
  }

  updateViewportSize(clientWidth: number, clientHeight: number) {
    const oldSize = getSize();
    const aspect = clientWidth / clientHeight;

    this.resizeContentFromBorder(aspect, oldSize.width !== clientWidth);
    this.updateProjectionMatrix();
  }

  getAntiZoomFactor(): number {
    return (HEIGHT / this.zoom) * ANTI_ZOOM_FACTOR_PER_VIEWPORT_HEIGHT;
  }

  public setZoom(zoom: number) {
    this.zoom = zoom;
    this.updateProjectionMatrix();
  }

  private getZoomMinCap() {
    if (!this.boundingBoxSizeOfActiveComponent) return DEFAULT_ZOOM_MIN_CAP;

    const maxWidth = this.boundingBoxSizeOfActiveComponent.width * ZOOM_MIN_CAP_FACTOR;
    const maxHeight = this.boundingBoxSizeOfActiveComponent.height * ZOOM_MIN_CAP_FACTOR;

    const zoomMinCapFromHeight = HEIGHT / maxHeight;
    const zoomMinCapFromWidth = HEIGHT / (maxWidth / this.aspect);

    // Returns the value that lets the user zoom out the most
    return Math.min(DEFAULT_ZOOM_MIN_CAP, Math.min(zoomMinCapFromHeight, zoomMinCapFromWidth));
  }

  private clampZoom(zoom: number) {
    const zoomMinCap = this.getZoomMinCap();
    return Math.min(Math.max(zoom, zoomMinCap), ZOOM_MAX_CAP);
  }

  public setBoundingBoxSizeOfActiveComponent(boundingBoxSizeOfActiveComponent: BoundingBoxSize | null) {
    this.boundingBoxSizeOfActiveComponent = boundingBoxSizeOfActiveComponent;
    const newZoom = this.clampZoom(this.zoom);
    if (newZoom !== this.zoom) {
      this.setZoom(newZoom);
    }
  }

  public resizeContentFromBorder(newAspect: number, shiftFromWidth: boolean) {
    const newZoom = shiftFromWidth ? this.zoom * (newAspect / this.aspect) : this.zoom;
    this.aspect = newAspect;
    this.zoom = this.clampZoom(newZoom);
  }

  public pan(screenXDelta: number, screenYDelta: number) {
    const mousePosInLocalCameraCoordinates = new Vector3(screenXDelta, screenYDelta, 0.5);
    this.worldToLocal(mousePosInLocalCameraCoordinates.unproject(this));
    mousePosInLocalCameraCoordinates.z = 0;
    const newPosition = this.localToWorld(mousePosInLocalCameraCoordinates);
    this.position.x = newPosition.x;
    this.position.y = newPosition.y;
    this.position.z = newPosition.z;
    this.updateProjectionMatrix();
  }

  public zoomCamera(screenPosX: number, screenPosY: number, zoomAmount: number) {
    const mousePosInLocalCameraCoordinates = new Vector3(screenPosX, screenPosY, 0.5);
    this.worldToLocal(mousePosInLocalCameraCoordinates.unproject(this));
    const newZoom = this.clampZoom(this.zoom * (1 - zoomAmount));
    const a = ((HEIGHT / newZoom) * this.aspect) / 2; // Distance from camera center X AFTER zoom
    const b = ((HEIGHT / this.zoom) * this.aspect) / 2; // Distance from camera center X BEFORE zoom
    const c = HEIGHT / newZoom / 2; // Distance from camera center Y AFTER zoom
    const d = HEIGHT / this.zoom / 2; // Distance from camera center Y BEFORE zoom
    const e = mousePosInLocalCameraCoordinates.x; // Mouse position x in local space
    const f = mousePosInLocalCameraCoordinates.y; // Mouse position y in local space
    // Calculation based on ratio using the shortest distance to edge
    const g = (-1 * a * e + b * e) / b; // New camera center x calculation
    const h = (-1 * c * f + d * f) / d; // New camera center y calculation
    const newPosition = this.localToWorld(new Vector3(g, h, 0));
    this.position.x = newPosition.x;
    this.position.y = newPosition.y;
    this.position.z = newPosition.z;
    this.zoom = newZoom;
    this.updateProjectionMatrix();
  }

  public updateProjectionMatrix() {
    this.left = (HEIGHT * this.aspect) / -2;
    this.right = (HEIGHT * this.aspect) / 2;
    this.top = HEIGHT / 2;
    this.bottom = HEIGHT / -2;
    super.updateProjectionMatrix();
    this.updateMatrix();
    this.updateMatrixWorld(false);

    updateAntiZoomFactor(this.getAntiZoomFactor());
  }

  public getCurrentBounds(): SceneObjectBounds {
    const height = HEIGHT / (this.zoom * (1 + FOCUS_ZOOM_PADDING));
    const width = height * this.aspect;

    return {
      position: { x: this.position.x, y: this.position.y },
      size: { width, height },
    };
  }

  public focusTo(cameraFocusBounds: SceneObjectBounds) {
    const zoomToFitWidth = HEIGHT / ((cameraFocusBounds.size.width / this.aspect) * (1 + FOCUS_ZOOM_PADDING));
    const zoomToFitHeight = HEIGHT / (cameraFocusBounds.size.height * (1 + FOCUS_ZOOM_PADDING));

    this.zoom = this.clampZoom(Math.min(zoomToFitWidth, zoomToFitHeight));
    this.position.x = cameraFocusBounds.position.x ?? this.position.x;
    this.position.y = cameraFocusBounds.position.y ?? this.position.y;

    this.updateProjectionMatrix();
  }
}
